// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import React from 'react';
import { _formatString } from '@internal/acs-ui-common';
import { Parser, ProcessNodeDefinitions, IsValidNodeDefinitions, ProcessingInstructionType } from 'html-to-react';
import Linkify from 'react-linkify';
import { ChatMessage } from '../../types/ChatMessage';
import { Link } from '@fluentui/react';
import { MessageThreadStrings } from '../MessageThread';
import LiveMessage from '../Announcer/LiveMessage';
import DOMPurify from 'dompurify';
type ChatMessageContentProps = {
  message: ChatMessage;
  strings: MessageThreadStrings;
};
type MessageContentWithLiveAriaProps = {
  message: ChatMessage;
  liveMessage: string;
  ariaLabel?: string;
  content: JSX.Element;
};

/** @private */
export const ChatMessageContent = (props: ChatMessageContentProps): JSX.Element => {
  switch (props.message.contentType) {
    case 'text':
      return MessageContentAsText(props);
    case 'html':
      return MessageContentAsRichTextHTML(props);
    case 'richtext/html':
      return MessageContentAsRichTextHTML(props);
    default:
      console.warn('unknown message content type');
      return <></>;
  }
};
const MessageContentWithLiveAria = (props: MessageContentWithLiveAriaProps): JSX.Element => {
  return <div data-ui-status={props.message.status} role="text" aria-label={props.ariaLabel}>
      <LiveMessage message={props.liveMessage} ariaLive="polite" />
      {props.content}
    </div>;
};
const MessageContentAsRichTextHTML = (props: ChatMessageContentProps): JSX.Element => {
  return <MessageContentWithLiveAria message={props.message} liveMessage={generateLiveMessage(props)} ariaLabel={messageContentAriaText(props)} content={processHtmlToReact(props)} />;
};
const MessageContentAsText = (props: ChatMessageContentProps): JSX.Element => {
  return <MessageContentWithLiveAria message={props.message} liveMessage={generateLiveMessage(props)} ariaLabel={messageContentAriaText(props)} content={<Linkify componentDecorator={(decoratedHref: string, decoratedText: string, key: number) => {
    return <Link target="_blank" href={decoratedHref} key={key}>
                {decoratedText}
              </Link>;
  }}>
          {props.message.content}
        </Linkify>} />;
};
// https://stackoverflow.com/questions/28899298/extract-the-text-out-of-html-string-using-javascript
const extractContent = (s: string): string => {
  const span = document.createElement('span');
  span.innerHTML = s;
  return span.textContent || span.innerText;
};
const generateLiveMessage = (props: ChatMessageContentProps): string => {
  const liveAuthor = _formatString(props.strings.liveAuthorIntro, {
    author: `${props.message.senderDisplayName}`
  });
  return `${props.message.editedOn ? props.strings.editedTag : ''} ${props.message.mine ? '' : liveAuthor} ${extractContent(props.message.content || '')} `;
};
const messageContentAriaText = (props: ChatMessageContentProps): string | undefined => {
  // Strip all html tags from the content for aria.

  return props.message.content ? props.message.mine ? _formatString(props.strings.messageContentMineAriaText, {
    message: DOMPurify.sanitize(props.message.content, {
      ALLOWED_TAGS: []
    })
  }) : _formatString(props.strings.messageContentAriaText, {
    author: `${props.message.senderDisplayName}`,
    message: DOMPurify.sanitize(props.message.content, {
      ALLOWED_TAGS: []
    })
  }) : undefined;
};
const processNodeDefinitions = ProcessNodeDefinitions();
const htmlToReactParser = Parser();
const processHtmlToReact = (props: ChatMessageContentProps): JSX.Element => {
  const steps: ProcessingInstructionType[] = [{
    // Process everything else in the default way
    shouldProcessNode: IsValidNodeDefinitions.alwaysValid,
    processNode: processNodeDefinitions.processDefaultNode
  }];
  return htmlToReactParser.parseWithInstructions(props.message.content ?? '', IsValidNodeDefinitions.alwaysValid, steps);
};"../../../../acs-ui-common/src"